import { GROWTH, multiplierAt } from '../shared/timing';

/**
 * Round clock — predicts the multiplier locally instead of being told it 20
 * times a second.
 *
 * The curve is a pure function of how long the round has been live, so once the
 * client knows the server's start time it can draw every frame itself. The
 * server sends a correction once a second; the only thing the client cannot know
 * is where the round crashes, which stays server-side until it happens.
 *
 * Two things stop prediction from ever being wrong in the player's favour:
 *  - the estimate follows the shared curve on the server-anchored clock, but may
 *    run at most one correction interval (plus a small latency margin) past the
 *    last value the server confirmed. Between corrections that ceiling sits just
 *    ahead of the curve, so it never bites; if corrections stop arriving, the
 *    display plateaus instead of racing on to a multiplier that never existed;
 *  - the moment `game:crashed` arrives the display is frozen at the server's
 *    crash point.
 *
 * A predicted number is never evidence of anything. Balances and cashouts only
 * ever change on an explicit server message.
 */

/** Weight for new offset samples. Low enough to ignore one bad round trip. */
const SMOOTHING = 0.2;
/** How often the server sends `game:sync` (backend/src/socket/gameLoop.js). */
export const SYNC_INTERVAL_MS = 1000;
/**
 * Slack on top of one sync interval: the correction's one-way latency, jitter,
 * and the curve's two-decimal flooring (worth ~65ms of growth near 1.00x).
 * Together they cap the lead at ~1.2s of growth, about 1.19x.
 */
export const LEAD_MARGIN_MS = 200;
const MAX_LEAD_MS = SYNC_INTERVAL_MS + LEAD_MARGIN_MS;

export interface RoundClockState {
    offsetMs: number;
    samples: number;
    lastServerMultiplier: number;
    lastSyncAt: number;
    startTime: number | null;
    frozenAt: number | null;
}

export class RoundClock {
    private offsetMs = 0;
    private samples = 0;
    private startTime: number | null = null;
    private lastServerMultiplier = 1;
    private lastSyncAt = 0;
    private frozen: number | null = null;
    /** Highest value shown this round, so an offset correction never steps the display backwards. */
    private shown = 1;

    /**
     * Fold in the server's clock reading.
     * @param serverNow epoch ms stamped by the server as it sent the message
     * @param roundTripMs measured round trip, if known; half of it is the one-way delay
     */
    observeServerTime(serverNow: number, roundTripMs = 0): void {
        if (!Number.isFinite(serverNow)) return;
        const estimate = serverNow + roundTripMs / 2 - Date.now();
        // The first reading sets the offset outright: averaging from zero would
        // take several seconds to converge on a device whose clock is badly wrong.
        this.offsetMs = this.samples === 0 ? estimate : this.offsetMs + SMOOTHING * (estimate - this.offsetMs);
        this.samples += 1;
    }

    /** Server time as this device best understands it. */
    now(): number {
        return Date.now() + this.offsetMs;
    }

    /** A new round is live. */
    startRound(startTime: number, serverNow?: number): void {
        if (serverNow !== undefined) this.observeServerTime(serverNow);
        this.startTime = startTime;
        this.lastServerMultiplier = 1;
        this.lastSyncAt = this.now();
        this.frozen = null;
        this.shown = 1;
    }

    /** The once-a-second correction. */
    observeSync(multiplier: number, serverNow: number): void {
        this.observeServerTime(serverNow);
        if (Number.isFinite(multiplier)) {
            this.lastServerMultiplier = Math.max(this.lastServerMultiplier, multiplier);
            // The multiplier was computed when the server stamped the message, so
            // the lead allowance is measured from then, not from arrival.
            this.lastSyncAt = Number.isFinite(serverNow) ? serverNow : this.now();
        }
    }

    /** The round is over: the display stops here regardless of the local estimate. */
    freeze(crashPoint: number): void {
        this.frozen = Number.isFinite(crashPoint) ? crashPoint : this.lastServerMultiplier;
    }

    /** Between rounds. */
    reset(): void {
        this.startTime = null;
        this.lastServerMultiplier = 1;
        this.frozen = null;
        this.shown = 1;
    }

    /**
     * What to draw right now. Safe to call every animation frame.
     */
    multiplier(): number {
        if (this.frozen !== null) return this.frozen;
        if (this.startTime === null) return 1;
        const now = this.now();
        const predicted = multiplierAt(this.startTime, now);
        // The ceiling is where the curve would be one correction interval (plus
        // margin) after the last confirmed value. With corrections arriving on
        // time it stays ahead of the prediction, so the display moves smoothly;
        // if they stop, or the device clock jumps, it plateaus there rather than
        // showing a multiplier the round may never have reached.
        const lead = Math.min(MAX_LEAD_MS, Math.max(0, now - this.lastSyncAt) + LEAD_MARGIN_MS);
        const ceiling = Math.max(1, this.lastServerMultiplier * Math.exp(GROWTH * lead / 1000));
        this.shown = Math.max(this.shown, Math.min(predicted, ceiling));
        return this.shown;
    }

    /** Seconds the round has been live, for animations that scale with time. */
    elapsedSeconds(): number {
        if (this.startTime === null) return 0;
        return Math.max(0, (this.now() - this.startTime) / 1000);
    }

    get isRunning(): boolean { return this.startTime !== null && this.frozen === null; }

    snapshot(): RoundClockState {
        return {
            offsetMs: this.offsetMs, samples: this.samples,
            lastServerMultiplier: this.lastServerMultiplier, lastSyncAt: this.lastSyncAt,
            startTime: this.startTime, frozenAt: this.frozen,
        };
    }
}

export const roundClock = new RoundClock();
